import { Box, Typography, Paper, Stack, LinearProgress, Chip } from '@mui/material';
import Grid from '@mui/material/Grid';
// ⚡ Path-based imports (6× faster than barrel imports)
import SpeedIcon from '@mui/icons-material/Speed';
import VolumeIcon from '@mui/icons-material/VolumeUp';
import MusicIcon from '@mui/icons-material/MusicNote';
import PianoIcon from '@mui/icons-material/Piano';
import WaveIcon from '@mui/icons-material/GraphicEq';
import { formatTempoPrimary, formatTempoRange, tempoHasRange } from '@/lib/audioAnalysis';

interface AudioFeaturesChartProps {
  data: {
    track?: {
      tempo?: number;
      tempo_min?: number;
      tempo_max?: number;
      loudness?: number;
      key?: number;
      mode?: number;
      time_signature?: number;
      duration?: number;
    };
  };
}

// Musical note names for key display (0-11 corresponds to C-B)
const NOTE_NAMES = ['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B'];

// Circle of fifths colors for each key
const KEY_COLORS: { [key: string]: string } = {
  'C': '#1ed760',   // Green
  'G': '#50e3c2',   // Cyan
  'D': '#509bf5',   // Blue
  'A': '#7c4dff',   // Purple
  'E': '#c44dff',   // Magenta
  'B': '#f5457d',   // Pink
  'F♯': '#ff6437',  // Orange
  'C♯': '#ffa726',  // Light Orange
  'G♯': '#ffca28',  // Yellow
  'D♯': '#cddc39',  // Lime
  'A♯': '#8bc34a',  // Light Green
  'F': '#4caf50',   // Dark Green
};

export function AudioFeaturesChart({ data }: AudioFeaturesChartProps) {
  if (!data.track) {
    return null;
  }

  const { tempo, loudness, time_signature, key, mode } = data.track;
  const keyName = key !== undefined ? NOTE_NAMES[key] : undefined;
  const modeName = mode !== undefined ? (mode === 1 ? 'Major' : 'Minor') : undefined;
  const keyColor = keyName ? KEY_COLORS[keyName] || '#1ed760' : '#1ed760';
  const tempoRange = formatTempoRange(data.track);
  const tempoLabel = tempoHasRange(data.track)
    ? `${formatTempoPrimary(data.track, 0)} (${tempoRange})`
    : formatTempoPrimary(data.track, 0);

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" fontWeight="bold" mb={1}>
        Audio Features
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Musical characteristics detected by librosa
      </Typography>
      
      <Grid container spacing={3}>
        {/* Tempo Card */}
        {tempo && (
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'radial-gradient(circle at top right, rgba(255,255,255,0.2), transparent)',
                }
              }}
            >
              <Stack spacing={2} position="relative">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SpeedIcon sx={{ fontSize: 32 }} />
                  <Typography variant="caption" sx={{ opacity: 0.9, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Tempo
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight="bold">
                  {tempo.toFixed(0)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {tempoRange
                    ? `BPM · ${tempoRange}`
                    : `BPM · ${tempo < 90 ? 'Slow' : tempo < 120 ? 'Moderate' : tempo < 140 ? 'Fast' : 'Very Fast'}`}
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min((tempo / 200) * 100, 100)} 
                  sx={{ 
                    height: 6, 
                    borderRadius: 3,
                    bgcolor: 'rgba(255,255,255,0.3)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: 'white',
                      borderRadius: 3,
                    }
                  }}
                />
              </Stack>
            </Paper>
          </Grid>
        )}

        {/* Loudness Card */}
        {loudness !== undefined && (
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                bgcolor: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'radial-gradient(circle at top right, rgba(255,255,255,0.2), transparent)',
                }
              }}
            >
              <Stack spacing={2} position="relative">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VolumeIcon sx={{ fontSize: 32 }} />
                  <Typography variant="caption" sx={{ opacity: 0.9, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Loudness
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight="bold">
                  {loudness.toFixed(1)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  dB · {loudness > -10 ? 'Loud' : loudness > -20 ? 'Moderate' : 'Quiet'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end', height: 40 }}>
                  {Array.from({ length: 12 }).map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        flex: 1,
                        bgcolor: 'rgba(255,255,255,0.3)',
                        borderRadius: 1,
                        height: `${20 + i * 5}%`,
                        opacity: ((loudness + 60) / 60) * 12 > i ? 1 : 0.3,
                        transition: 'opacity 0.3s',
                      }}
                    />
                  ))}
                </Box>
              </Stack>
            </Paper>
          </Grid>
        )}

        {/* Time Signature Card */}
        {time_signature && (
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                bgcolor: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'radial-gradient(circle at top right, rgba(255,255,255,0.2), transparent)',
                }
              }}
            >
              <Stack spacing={2} position="relative">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WaveIcon sx={{ fontSize: 32 }} />
                  <Typography variant="caption" sx={{ opacity: 0.9, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Time Signature
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight="bold">
                  {time_signature}/4
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {time_signature === 4 ? 'Common time · Standard beat' : 
                   time_signature === 3 ? 'Waltz time · Three beats' :
                   'Uncommon · Complex rhythm'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {Array.from({ length: time_signature }).map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: 'white',
                      }}
                    />
                  ))}
                </Box>
              </Stack>
            </Paper>
          </Grid>
        )}

        {/* Key & Mode Card */}
        {keyName && modeName && (
          <Grid size={{ xs: 12, sm: 12, md: 8 }}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                bgcolor: keyColor,
                color: 'white',
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'radial-gradient(circle at top right, rgba(255,255,255,0.2), transparent)',
                }
              }}
            >
              <Grid container spacing={3} position="relative">
                <Grid size={{ xs: 12, md: 6 }}>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PianoIcon sx={{ fontSize: 32 }} />
                      <Typography variant="caption" sx={{ opacity: 0.9, textTransform: 'uppercase', letterSpacing: 1 }}>
                        Musical Key
                      </Typography>
                    </Box>
                    <Typography variant="h2" fontWeight="bold">
                      {keyName}
                    </Typography>
                    <Chip 
                      label={modeName}
                      sx={{ 
                        bgcolor: 'rgba(255,255,255,0.25)', 
                        color: 'white',
                        fontWeight: 'bold',
                        width: 'fit-content',
                        fontSize: '1rem',
                        height: 36,
                      }}
                    />
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                      {modeName === 'Major' 
                        ? 'Bright, happy, and uplifting sound' 
                        : 'Melancholic, dark, and emotional sound'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {NOTE_NAMES.map((note, i) => (
                        <Box
                          key={note}
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: i === key ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.2)',
                            color: i === key ? keyColor : 'white',
                            fontSize: '0.75rem',
                            fontWeight: i === key ? 'bold' : 'normal',
                            transition: 'all 0.2s',
                          }}
                        >
                          {note}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Summary Card */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              bgcolor: 'action.hover',
              borderRadius: 3,
              height: '100%',
            }}
          >
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MusicIcon color="primary" sx={{ fontSize: 32 }} />
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  Summary
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                This track is in <strong>{keyName} {modeName}</strong> at <strong>{tempoLabel}</strong> with a <strong>{time_signature}/4</strong> time signature.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The overall loudness is <strong>{loudness?.toFixed(1)} dB</strong>, making it a {loudness && loudness > -10 ? 'loud and energetic' : loudness && loudness > -20 ? 'moderately dynamic' : 'quiet and subtle'} recording.
              </Typography>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
}
